'use client'

import * as React from 'react'
import { PencilIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

function PropertyList({ className, ...props }: React.ComponentProps<'dl'>) {
  return <dl data-slot="property-list" className={cn('divide-y border-y', className)} {...props} />
}

function PropertyRow({
  label,
  action,
  description,
  children,
  className,
  ...props
}: Omit<React.ComponentProps<'div'>, 'children'> & {
  label: React.ReactNode
  action?: React.ReactNode
  description?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div
      data-slot="property-row"
      className={cn(
        'grid min-h-10 grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1 px-3 py-2',
        'sm:grid-cols-[minmax(7rem,0.35fr)_minmax(0,1fr)_auto] sm:items-center',
        className,
      )}
      {...props}
    >
      <dt className="min-w-0 truncate text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="col-span-2 min-w-0 text-sm sm:col-span-1">
        <div className="min-w-0 truncate">{children}</div>
        {description && <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>}
      </dd>
      {action && (
        <dd aria-label="Property actions" className="col-start-2 row-start-1 sm:col-start-3">
          {action}
        </dd>
      )}
    </div>
  )
}

function PropertyEditButton({
  label,
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'children' | 'size'> & {
  label: string
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          data-slot="property-edit-button"
          {...props}
        >
          <PencilIcon aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

export { PropertyEditButton, PropertyList, PropertyRow }
